import type { Task, TaskMember, TaskPriority } from './types';

export type TaskViewId = 'mine' | 'today' | 'overdue' | 'week' | 'all' | 'done';
export type TaskGroupBy = 'faelligkeit' | 'prioritaet' | 'person';
export type TaskStatus = 'overdue' | 'open' | 'done';
/** Farbton für Fälligkeit, Gruppen und Kennzahlen — die Klassen vergibt die UI. */
export type TaskTone = 'overdue' | 'today' | 'default';

export type TaskViewContext = { userId: string | null; today: string };

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export const PRIORITY_META: Record<TaskPriority, { label: string; rank: number }> = {
  hoch: { label: 'Hoch', rank: 0 },
  mittel: { label: 'Mittel', rank: 1 },
  niedrig: { label: 'Niedrig', rank: 2 },
};

export const PRIORITY_ORDER: TaskPriority[] = ['hoch', 'mittel', 'niedrig'];

export const STATUS_META: Record<TaskStatus, { label: string; rank: number }> = {
  overdue: { label: 'Überfällig', rank: 0 },
  open: { label: 'Offen', rank: 1 },
  done: { label: 'Erledigt', rank: 2 },
};

export function localYmd(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseYmd(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Tage von `today` bis `ymd` (negativ = Vergangenheit). */
export function dayDiff(ymd: string, today: string): number {
  return Math.round((parseYmd(ymd).getTime() - parseYmd(today).getTime()) / 864e5);
}

export function addDays(ymd: string, days: number): string {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + days);
  return localYmd(d);
}

export function formatDdMm(ymd: string): string {
  const d = parseYmd(ymd);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

export function formatWeekdayDate(ymd: string): string {
  const d = parseYmd(ymd);
  const long = d.toLocaleDateString('de-DE', { weekday: 'long' });
  return `${long}, ${formatDdMm(ymd)}${d.getFullYear()}`;
}

export function isOverdue(t: Task, today: string): boolean {
  return !t.done && Boolean(t.due_date) && dayDiff(t.due_date as string, today) < 0;
}

export function isDueToday(t: Task, today: string): boolean {
  return !t.done && Boolean(t.due_date) && dayDiff(t.due_date as string, today) === 0;
}

/** Erledigt in den letzten 7 Tagen (inkl. heute). */
export function isDoneRecently(t: Task, today: string): boolean {
  if (!t.done || !t.done_at) return false;
  return dayDiff(localYmd(new Date(t.done_at)), today) >= -6;
}

export function taskStatus(t: Task, today: string): TaskStatus {
  if (t.done) return 'done';
  return isOverdue(t, today) ? 'overdue' : 'open';
}

export type TaskViewDef = {
  id: TaskViewId;
  label: string;
  sub: (ctx: TaskViewContext) => string;
  test: (t: Task, ctx: TaskViewContext) => boolean;
};

export const TASK_VIEWS: TaskViewDef[] = [
  {
    id: 'mine',
    label: 'Meine To-dos',
    sub: () => 'Dir zugewiesen, offen',
    test: (t, ctx) => !t.done && t.assigned_user_id === ctx.userId,
  },
  {
    id: 'today',
    label: 'Heute',
    sub: () => 'Heute fällig und überfällig',
    test: (t, ctx) => !t.done && Boolean(t.due_date) && dayDiff(t.due_date as string, ctx.today) <= 0,
  },
  {
    id: 'overdue',
    label: 'Überfällig',
    sub: () => 'Fälligkeitsdatum überschritten',
    test: (t, ctx) => isOverdue(t, ctx.today),
  },
  {
    id: 'week',
    label: 'Nächste 7 Tage',
    sub: (ctx) => `Fällig bis einschließlich ${formatDdMm(addDays(ctx.today, 6))}`,
    test: (t, ctx) => !t.done && Boolean(t.due_date) && dayDiff(t.due_date as string, ctx.today) <= 6,
  },
  {
    id: 'all',
    label: 'Alle offenen',
    sub: () => 'Alle offenen To-dos im Team',
    test: (t) => !t.done,
  },
  {
    id: 'done',
    label: 'Erledigt',
    sub: () => 'Abgeschlossene To-dos',
    test: (t) => t.done,
  },
];

export function getTaskView(id: TaskViewId): TaskViewDef {
  return TASK_VIEWS.find((v) => v.id === id) ?? TASK_VIEWS[0];
}

/** URL-Segmente unter /aufgaben/:viewSlug */
export const VIEW_SLUGS: Record<TaskViewId | 'team', string> = {
  mine: 'meine',
  today: 'heute',
  overdue: 'ueberfaellig',
  week: 'woche',
  all: 'offen',
  done: 'erledigt',
  team: 'team',
};

export function viewFromSlug(slug: string | undefined): TaskViewId | 'team' | null {
  if (!slug) return null;
  const hit = (Object.entries(VIEW_SLUGS) as [TaskViewId | 'team', string][]).find(([, s]) => s === slug);
  return hit ? hit[0] : null;
}

export function memberName(member: TaskMember | undefined | null): string {
  if (!member) return 'Nicht zugewiesen';
  return member.display_name?.trim() || member.email || 'Unbekannt';
}

export function matchesTaskQuery(t: Task, query: string, members?: Map<string, TaskMember>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const who = t.assigned_user_id && members ? memberName(members.get(t.assigned_user_id)) : '';
  return [t.title, t.display_id, t.lead?.display_id, t.lead?.lead_title, t.lead?.name, who]
    .some((v) => String(v ?? '').toLowerCase().includes(q));
}

export type DueLabel = { text: string; tone: TaskTone };

export function dueLabel(t: Task, today: string): DueLabel {
  if (!t.due_date) return { text: '', tone: 'default' };
  const d = dayDiff(t.due_date, today);
  const dt = parseYmd(t.due_date);
  if (t.done) return { text: formatDdMm(t.due_date), tone: 'default' };
  if (d < 0) return { text: `Überfällig · ${formatDdMm(t.due_date)}`, tone: 'overdue' };
  if (d === 0) return { text: 'Heute', tone: 'today' };
  if (d === 1) return { text: 'Morgen', tone: 'default' };
  if (d < 7) return { text: `${WEEKDAYS[dt.getDay()]}, ${formatDdMm(t.due_date)}`, tone: 'default' };
  return { text: `${formatDdMm(t.due_date)}${dt.getFullYear()}`, tone: 'default' };
}

export function compareByDueThenPriority(a: Task, b: Task): number {
  return (
    (a.due_date || '9999').localeCompare(b.due_date || '9999') ||
    PRIORITY_META[a.priority].rank - PRIORITY_META[b.priority].rank
  );
}

export type TaskGroup = { key: string; label: string; tone: TaskTone; tasks: Task[] };

export function groupTasks(
  tasks: Task[],
  groupBy: TaskGroupBy,
  ctx: { today: string; members: TaskMember[] },
): TaskGroup[] {
  const { today } = ctx;
  type Bucket = { key: string; label: string; tone: TaskTone; test: (t: Task) => boolean };
  let buckets: Bucket[];
  if (groupBy === 'prioritaet') {
    buckets = PRIORITY_ORDER.map((p) => ({
      key: p,
      label: PRIORITY_META[p].label,
      tone: 'default',
      test: (t) => t.priority === p,
    }));
  } else if (groupBy === 'person') {
    const ids = new Set(ctx.members.map((m) => m.id));
    buckets = [
      ...ctx.members.map<Bucket>((m) => ({
        key: m.id,
        label: memberName(m),
        tone: 'default',
        test: (t) => t.assigned_user_id === m.id,
      })),
      {
        key: '__none',
        label: 'Nicht zugewiesen',
        tone: 'default',
        test: (t) => !t.assigned_user_id || !ids.has(t.assigned_user_id),
      },
    ];
  } else {
    const diff = (t: Task) => (t.due_date ? dayDiff(t.due_date, today) : null);
    buckets = [
      { key: 'overdue', label: 'Überfällig', tone: 'overdue', test: (t) => { const d = diff(t); return d !== null && d < 0; } },
      { key: 'today', label: 'Heute', tone: 'today', test: (t) => diff(t) === 0 },
      { key: 'tomorrow', label: 'Morgen', tone: 'default', test: (t) => diff(t) === 1 },
      { key: 'week', label: 'Diese Woche', tone: 'default', test: (t) => { const d = diff(t); return d !== null && d > 1 && d <= 6; } },
      { key: 'later', label: 'Später', tone: 'default', test: (t) => { const d = diff(t); return d !== null && d > 6; } },
      { key: 'none', label: 'Ohne Datum', tone: 'default', test: (t) => !t.due_date },
    ];
  }
  return buckets
    .map((b) => ({
      key: b.key,
      label: b.label,
      tone: b.tone,
      tasks: tasks.filter(b.test).sort(compareByDueThenPriority),
    }))
    .filter((g) => g.tasks.length > 0);
}

/** Ansicht „Erledigt": nach Erledigt-Datum gruppiert, neueste zuerst. */
export function groupDoneTasks(tasks: Task[], today: string): TaskGroup[] {
  const doneDay = (t: Task) => (t.done_at ? dayDiff(localYmd(new Date(t.done_at)), today) : null);
  const buckets: { key: string; label: string; test: (d: number | null) => boolean }[] = [
    { key: 'today', label: 'Heute', test: (d) => d === 0 },
    { key: 'yesterday', label: 'Gestern', test: (d) => d === -1 },
    { key: 'week', label: 'Diese Woche', test: (d) => d !== null && d < -1 && d >= -6 },
    { key: 'older', label: 'Früher', test: (d) => d === null || d < -6 },
  ];
  const sorted = [...tasks].sort((a, b) => (b.done_at ?? '').localeCompare(a.done_at ?? ''));
  return buckets
    .map((b) => ({
      key: b.key,
      label: b.label,
      tone: 'default' as TaskTone,
      tasks: sorted.filter((t) => b.test(doneDay(t))),
    }))
    .filter((g) => g.tasks.length > 0);
}

/** „Erledigt 05.10." bzw. „Erledigt heute" für erledigte Zeilen. */
export function doneLabel(t: Task, today: string): string {
  if (!t.done_at) return 'Erledigt';
  const ymd = localYmd(new Date(t.done_at));
  const d = dayDiff(ymd, today);
  if (d === 0) return 'Erledigt heute';
  if (d === -1) return 'Erledigt gestern';
  return `Erledigt ${formatDdMm(ymd)}`;
}

export type TeamMemberStats = {
  member: TaskMember;
  open: number;
  overdue: number;
  doneRecently: number;
};

export function teamStats(tasks: Task[], members: TaskMember[], today: string): TeamMemberStats[] {
  return members.map((member) => {
    const mine = tasks.filter((t) => t.assigned_user_id === member.id);
    return {
      member,
      open: mine.filter((t) => !t.done).length,
      overdue: mine.filter((t) => isOverdue(t, today)).length,
      doneRecently: mine.filter((t) => isDoneRecently(t, today)).length,
    };
  });
}

export type LeadProgress = {
  lead: NonNullable<Task['lead']>;
  total: number;
  done: number;
  overdue: number;
};

export function leadProgress(tasks: Task[], today: string): LeadProgress[] {
  const byLead = new Map<string, LeadProgress>();
  for (const t of tasks) {
    if (!t.lead) continue;
    const p = byLead.get(t.lead.id) ?? { lead: t.lead, total: 0, done: 0, overdue: 0 };
    p.total += 1;
    if (t.done) p.done += 1;
    if (isOverdue(t, today)) p.overdue += 1;
    byLead.set(t.lead.id, p);
  }
  return [...byLead.values()].sort(
    (a, b) => b.overdue - a.overdue || b.total - b.done - (a.total - a.done),
  );
}

export type TeamStatusFilter = 'all' | 'open' | 'overdue' | 'today' | 'done' | 'done7';

export function matchesStatusFilter(t: Task, filter: TeamStatusFilter, today: string): boolean {
  switch (filter) {
    case 'open':
      return !t.done;
    case 'overdue':
      return isOverdue(t, today);
    case 'today':
      return isDueToday(t, today);
    case 'done':
      return t.done;
    case 'done7':
      return isDoneRecently(t, today);
    default:
      return true;
  }
}

export type TeamSortKey = 'title' | 'lead' | 'who' | 'due' | 'prio' | 'status';

export function compareTasks(
  key: TeamSortKey,
  a: Task,
  b: Task,
  ctx: { today: string; members: Map<string, TaskMember> },
): number {
  switch (key) {
    case 'title':
      return a.title.localeCompare(b.title, 'de');
    case 'lead':
      return (a.lead?.lead_title || a.lead?.name || '~').localeCompare(b.lead?.lead_title || b.lead?.name || '~', 'de');
    case 'who': {
      const an = a.assigned_user_id ? memberName(ctx.members.get(a.assigned_user_id)) : '~';
      const bn = b.assigned_user_id ? memberName(ctx.members.get(b.assigned_user_id)) : '~';
      return an.localeCompare(bn, 'de');
    }
    case 'prio':
      return PRIORITY_META[a.priority].rank - PRIORITY_META[b.priority].rank;
    case 'status':
      return STATUS_META[taskStatus(a, ctx.today)].rank - STATUS_META[taskStatus(b, ctx.today)].rank;
    case 'due':
    default:
      return (a.due_date || '9999').localeCompare(b.due_date || '9999');
  }
}

export function leadLabel(lead: Task['lead']): string {
  if (!lead) return '';
  const title = lead.lead_title?.trim() || 'Anfrage';
  return lead.name?.trim() ? `${title} · ${lead.name.trim()}` : title;
}

export function countLabel(n: number): string {
  return `${n} ${n === 1 ? 'To-do' : 'To-dos'}`;
}

/** Rechte-Stufen wie auf dem Server (server/services/tasks.ts): eigene ⊂ alle ansehen ⊂ alle bearbeiten. */
export type TaskPermissions = { userId: string | null; own: boolean; viewAll: boolean; editAll: boolean };

export function isOwnTaskFor(t: Pick<Task, 'assigned_user_id' | 'created_by'>, userId: string | null): boolean {
  return Boolean(userId) && (t.assigned_user_id === userId || t.created_by === userId);
}

export function canEditTaskWith(p: TaskPermissions, t: Pick<Task, 'assigned_user_id' | 'created_by'>): boolean {
  return p.editAll || (p.own && isOwnTaskFor(t, p.userId));
}
